import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  addAdvisorContextItem,
  addAdvisorSymbolContext,
  clearAdvisorContextItems,
  getAdvisorAnalyses,
  getAdvisorChatHistory,
  getAdvisorContextSelection,
  getAdvisorContextItems,
  getAdvisorDeepScanStatus,
  getAdvisorWorkerUsage,
  getInstrumentSearch,
  addPositionMonitorSymbol,
  postAdvisorChat,
  postAdvisorChatStream,
  putAdvisorContextSelection,
  removePositionMonitorSymbol,
  removeAdvisorContextItem,
} from "../api";
import { formatIstDateTime } from "../utils/time";
import { useMediaQuery } from "../hooks/useMediaQuery";

const classifyAllOpenPositionsPrompt = "Classify all current open positions as HOLD, WATCH, TIGHTEN_STOP, PARTIAL_EXIT, or EXIT with reasons.";

const quickPrompts = [
  classifyAllOpenPositionsPrompt,
  "Which position has the highest profit giveback?",
  "Summarize the latest AI analysis for my portfolio.",
  "Which stops should I watch closely?",
];

const isBroadOpenPositionMessage = (message = "") => {
  const text = String(message || "").toLowerCase();
  return (
    /\ball\s+(current\s+)?open\s+positions\b/.test(text) ||
    /\b(current\s+)?open\s+positions\b/.test(text) ||
    /\bportfolio\b/.test(text) ||
    /\bholdings?\b/.test(text) ||
    /\bwhich\s+position\b/.test(text) ||
    /\bpositions?\b.*\b(giveback|risk|risky|stop|stops|watch|sell|exit|hold)\b/.test(text) ||
    /\b(giveback|risk|risky|stop|stops|watch|sell|exit|hold)\b.*\bpositions?\b/.test(text)
  );
};

const riskColor = (risk) => {
  const r = String(risk || "").toUpperCase();
  if (r === "HIGH") return "#ef4444";
  if (r === "MEDIUM") return "#f59e0b";
  if (r === "LOW") return "#22c55e";
  return "#94a3b8";
};

const shortProviderLabel = (provider) => {
  const value = String(provider || "").toLowerCase();
  if (value.startsWith("cloudflare_worker")) return "Cloudflare";
  if (value === "cloudflare_workers_ai") return "Cloudflare";
  if (value.startsWith("gemini")) return "Gemini";
  if (value.startsWith("groq")) return "Groq";
  return provider ? String(provider) : "AI";
};

const messageAiLabel = (msg, fallbackModel = "") => {
  const model = msg.model || fallbackModel;
  const provider = shortProviderLabel(msg.provider);
  if (model && provider) return `${provider} - ${model}`;
  return model || provider || "AI Advisor";
};

const isMarkdownTableSeparator = (line) =>
  /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/.test(String(line || ""));

const splitMarkdownTableRow = (line) => String(line || "")
  .trim()
  .replace(/^\|/, "")
  .replace(/\|$/, "")
  .split("|")
  .map((cell) => cell.trim());

const escapeRegExp = (value) => String(value || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const advisorActionLabels = ["PARTIAL_EXIT", "TIGHTEN_STOP", "EXIT", "WATCH", "HOLD"];

const getAdvisorActionGroups = (text, symbols) => {
  const groups = advisorActionLabels.reduce((acc, action) => ({ ...acc, [action]: [] }), {});
  const uniqueSymbols = Array.from(new Set((Array.isArray(symbols) ? symbols : [])
    .map((symbol) => String(symbol || "").toUpperCase().trim())
    .filter(Boolean)));
  const lines = String(text || "").split(/\r?\n/);

  uniqueSymbols.forEach((symbol) => {
    const escapedSymbol = escapeRegExp(symbol);
    for (const line of lines) {
      const upperLine = String(line || "").toUpperCase();
      const symbolThenAction = upperLine.match(new RegExp(`\\b${escapedSymbol}\\b\\s*[-:\\u2013\\u2014]\\s*(${advisorActionLabels.join("|")})\\b`));
      const actionThenSymbol = upperLine.match(new RegExp(`\\b(${advisorActionLabels.join("|")})\\b\\s*[-:\\u2013\\u2014]\\s*\\b${escapedSymbol}\\b`));
      const action = symbolThenAction?.[1] || actionThenSymbol?.[1] || null;
      if (action && groups[action] && !groups[action].includes(symbol)) groups[action].push(symbol);
    }
  });

  return groups;
};

const renderInlineMarkdown = (text, keyPrefix = "md") => {
  const parts = String(text || "").split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, index) => {
    const bold = part.match(/^\*\*([^*]+)\*\*$/);
    if (bold) return <strong key={`${keyPrefix}-b-${index}`} style={{ color: "#f8fafc" }}>{bold[1]}</strong>;
    return <React.Fragment key={`${keyPrefix}-t-${index}`}>{part}</React.Fragment>;
  });
};

const MarkdownMessage = ({ text, isMobile }) => {
  const lines = String(text || "").split(/\r?\n/);
  const blocks = [];
  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];
    const next = lines[i + 1];
    if (line.trim().startsWith("|") && isMarkdownTableSeparator(next)) {
      const headers = splitMarkdownTableRow(line);
      const rows = [];
      i += 2;
      while (i < lines.length && lines[i].trim().startsWith("|")) {
        rows.push(splitMarkdownTableRow(lines[i]));
        i += 1;
      }
      i -= 1;
      blocks.push({ type: "table", headers, rows });
    } else if (/^\s*---+\s*$/.test(line)) {
      blocks.push({ type: "rule" });
    } else if (/^\s*#{1,4}\s+/.test(line)) {
      blocks.push({ type: "heading", text: line.replace(/^\s*#{1,4}\s+/, "") });
    } else if (/^\s*[-*]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*]\s+/, ""));
        i += 1;
      }
      i -= 1;
      blocks.push({ type: "list", items });
    } else if (line.trim()) {
      const paragraph = [line.trim()];
      while (i + 1 < lines.length && lines[i + 1].trim() && !lines[i + 1].trim().startsWith("|") && !/^\s*(#{1,4}\s+|[-*]\s+|---+\s*$)/.test(lines[i + 1])) {
        i += 1;
        paragraph.push(lines[i].trim());
      }
      blocks.push({ type: "paragraph", text: paragraph.join(" ") });
    }
  }

  return (
    <div style={{ display: "grid", gap: 8, minWidth: 0, maxWidth: "100%", overflowWrap: "anywhere", wordBreak: "break-word" }}>
      {blocks.map((block, index) => {
        if (block.type === "table") {
          return (
            <div key={index} style={{ overflowX: "auto", maxWidth: "100%" }}>
              <table style={{ width: "100%", minWidth: isMobile ? 620 : 0, borderCollapse: "collapse", fontSize: 11 }}>
                <thead>
                  <tr>
                    {block.headers.map((header, cellIndex) => (
                      <th key={cellIndex} style={{ borderBottom: "1px solid #334155", color: "#94a3b8", textAlign: "left", padding: "6px 8px", whiteSpace: "nowrap" }}>
                        {renderInlineMarkdown(header, `h-${index}-${cellIndex}`)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {block.rows.map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      {block.headers.map((_, cellIndex) => (
                        <td key={cellIndex} style={{ borderBottom: "1px solid #0f172a", color: "#cbd5e1", padding: "7px 8px", verticalAlign: "top" }}>
                          {renderInlineMarkdown(row[cellIndex] || "", `r-${index}-${rowIndex}-${cellIndex}`)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
        if (block.type === "list") {
          return (
            <ul key={index} style={{ margin: "0 0 0 18px", padding: 0, minWidth: 0 }}>
              {block.items.map((item, itemIndex) => <li key={itemIndex}>{renderInlineMarkdown(item, `li-${index}-${itemIndex}`)}</li>)}
            </ul>
          );
        }
        if (block.type === "heading") {
          return <div key={index} style={{ color: "#f8fafc", fontWeight: 800, fontSize: 13 }}>{renderInlineMarkdown(block.text, `hd-${index}`)}</div>;
        }
        if (block.type === "rule") {
          return <div key={index} style={{ borderTop: "1px solid #1e293b", margin: "2px 0" }} />;
        }
        return <div key={index}>{renderInlineMarkdown(block.text, `p-${index}`)}</div>;
      })}
    </div>
  );
};

const fmtNumber = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "-";
  return n.toLocaleString("en-IN", { maximumFractionDigits: 0 });
};

export default function AdvisorChat({ botStatus }) {
  const isMobile = useMediaQuery("(max-width: 760px)");
  const [analyses, setAnalyses] = useState({});
  const [deepScan, setDeepScan] = useState(null);
  const [workerUsage, setWorkerUsage] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [contextItems, setContextItems] = useState([]);
  const [contextStatus, setContextStatus] = useState(null);
  const [symbolSearch, setSymbolSearch] = useState("");
  const [symbolMatches, setSymbolMatches] = useState([]);
  const [symbolSearchLoading, setSymbolSearchLoading] = useState(false);
  const [addingContextId, setAddingContextId] = useState(null);
  const [monitorActionId, setMonitorActionId] = useState(null);
  const [selectedContextItemIds, setSelectedContextItemIds] = useState([]);
  const [expandedContextItemIds, setExpandedContextItemIds] = useState([]);
  const introMessage = useMemo(() => ({
    id: "intro",
    role: "assistant",
    text: "Ask me about open positions, risk, giveback, stops, or the latest deep-scan analysis. I will only advise; I will not place or change trades.",
    createdAt: new Date().toISOString(),
  }), []);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [chatModelOverride, setChatModelOverride] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [historyBefore, setHistoryBefore] = useState(null);
  const [historyDays, setHistoryDays] = useState("3");
  const [hasMoreHistory, setHasMoreHistory] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [symbolsText, setSymbolsText] = useState("");
  const [contextOptions, setContextOptions] = useState({
    includePositions: false,
    includeTechnicals: false,
    includeStops: false,
    includeEntryStrategy: false,
    includeAiAnalyses: false,
    includeSavedContext: false,
    includeRiskConfig: false,
    includeRecentTrades: false,
  });
  const endRef = useRef(null);
  const messagesRef = useRef(null);
  const selectionLoadedRef = useRef(false);
  const shouldAutoScrollRef = useRef(true);
  const loadingOlderRef = useRef(false);

  const isNearChatBottom = (el) => {
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  const loadContext = async () => {
    try {
      const [analysisPayload, scanStatus] = await Promise.all([
        getAdvisorAnalyses(),
        getAdvisorDeepScanStatus(),
      ]);
      setAnalyses(analysisPayload?.analyses || {});
      setDeepScan(scanStatus || null);
    } catch (err) {
      setError(err?.message || "Failed to load advisor context");
    }
  };

  const loadWorkerUsage = async (force = false) => {
    setUsageLoading(true);
    try {
      const usagePayload = await getAdvisorWorkerUsage(force);
      setWorkerUsage(usagePayload || null);
    } catch (err) {
      setWorkerUsage((prev) => prev || { error: err?.message || "Usage unavailable" });
    } finally {
      setUsageLoading(false);
    }
  };

  const loadSavedContext = async () => {
    try {
      const [contextPayload, selectionPayload] = await Promise.all([
        getAdvisorContextItems(),
        getAdvisorContextSelection(),
      ]);
      const items = Array.isArray(contextPayload?.items) ? contextPayload.items : [];
      const validIds = new Set(items.map((item) => item.id));
      const selection = selectionPayload?.selection || {};
      const persistedOptions = selection.contextOptions && typeof selection.contextOptions === "object"
        ? selection.contextOptions
        : {};
      setContextItems(items);
      setContextOptions((prev) => ({
        ...prev,
        ...Object.fromEntries(Object.keys(prev).map((key) => [key, persistedOptions[key] === true])),
      }));
      setSelectedContextItemIds((Array.isArray(selection.selectedContextItemIds) ? selection.selectedContextItemIds : [])
        .map((id) => String(id || "").trim())
        .filter((id) => id && validIds.has(id)));
      setSymbolsText(String(selection.symbolsText || ""));
      if (["1", "2", "3", "7", "10", "30"].includes(String(selection.historyDays || ""))) {
        setHistoryDays(String(selection.historyDays));
      }
      selectionLoadedRef.current = true;
    } catch (err) {
      selectionLoadedRef.current = true;
      setContextStatus({ type: "error", text: err?.message || "Failed to load saved context" });
    }
  };

  useEffect(() => {
    loadContext();
    loadSavedContext();
    const t = setInterval(loadContext, 10000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const q = symbolSearch.trim();
    if (!q) {
      setSymbolMatches([]);
      return undefined;
    }
    setSymbolSearchLoading(true);
    const timer = setTimeout(async () => {
      try {
        const payload = await getInstrumentSearch(q);
        if (!cancelled) setSymbolMatches(Array.isArray(payload?.matches) ? payload.matches.slice(0, 8) : []);
      } catch (_) {
        if (!cancelled) setSymbolMatches([]);
      } finally {
        if (!cancelled) setSymbolSearchLoading(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [symbolSearch]);

  useEffect(() => {
    loadWorkerUsage();
    const t = setInterval(() => loadWorkerUsage(), 300000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const ids = new Set(contextItems.map((item) => item.id));
    setSelectedContextItemIds((prev) => prev.filter((id) => ids.has(id)));
    setExpandedContextItemIds((prev) => prev.filter((id) => ids.has(id)));
  }, [contextItems]);

  useEffect(() => {
    if (!selectionLoadedRef.current) return undefined;
    const timer = setTimeout(() => {
      putAdvisorContextSelection({
        contextOptions,
        selectedContextItemIds,
        symbolsText,
        historyDays,
      }).catch((err) => {
        setContextStatus({ type: "error", text: err?.message || "Failed to save context selection" });
      });
    }, 250);
    return () => clearTimeout(timer);
  }, [contextOptions, selectedContextItemIds, symbolsText, historyDays]);

  useEffect(() => {
    const loadInitialHistory = async () => {
      setLoadingHistory(true);
      try {
        const payload = await getAdvisorChatHistory({ limit: 2, days: historyDays || undefined });
        setMessages(payload?.messages?.length ? payload.messages : [introMessage]);
        setHistoryBefore(payload?.nextBefore || null);
        setHasMoreHistory(!!payload?.hasMore);
        setTimeout(() => endRef.current?.scrollIntoView({ block: "end" }), 0);
      } catch (err) {
        setError(err?.message || "Failed to load advisor chat history");
        setMessages([introMessage]);
      } finally {
        setLoadingHistory(false);
      }
    };
    loadInitialHistory();
  }, [historyDays, introMessage]);

  useEffect(() => {
    if (loadingOlderRef.current || !shouldAutoScrollRef.current) return;
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, sending]);

  const analysisRows = useMemo(() => Object.values(analyses || {})
    .sort((a, b) => Number(b.exitUrgency || 0) - Number(a.exitUrgency || 0))
    .slice(0, 8), [analyses]);

  const selectedContextOptions = useMemo(() => ({
    ...contextOptions,
    historyDays,
    savedContextItemIds: selectedContextItemIds,
    symbols: symbolsText
      .split(/[,\s]+/)
      .map((s) => s.trim().toUpperCase())
      .filter(Boolean),
  }), [contextOptions, historyDays, selectedContextItemIds, symbolsText]);

  const contextLabels = [
    contextOptions.includePositions && "positions",
    contextOptions.includeTechnicals && "technicals",
    contextOptions.includeStops && "stops",
    contextOptions.includeEntryStrategy && "entry",
    contextOptions.includeAiAnalyses && "AI analysis",
    contextOptions.includeSavedContext && "saved context",
    contextOptions.includeRiskConfig && "risk",
    contextOptions.includeRecentTrades && "trades",
  ].filter(Boolean);

  const setContextFlag = (key) => {
    setContextOptions((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const allContextKeys = [
    "includePositions",
    "includeTechnicals",
    "includeStops",
    "includeEntryStrategy",
    "includeAiAnalyses",
    "includeSavedContext",
    "includeRiskConfig",
    "includeRecentTrades",
  ];
  const allBaseContextSelected = allContextKeys.every((key) => !!contextOptions[key]);
  const setAllBaseContext = (checked) => {
    setContextOptions((prev) => Object.fromEntries(allContextKeys.map((key) => [key, checked])));
  };
  const allSavedContextSelected = contextItems.length > 0 && contextItems.every((item) => selectedContextItemIds.includes(item.id));
  const toggleSavedContextItem = (id) => {
    setSelectedContextItemIds((prev) => prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]);
  };
  const setAllSavedContextItems = (checked) => {
    setSelectedContextItemIds(checked ? contextItems.map((item) => item.id) : []);
  };
  const toggleExpandedContextItem = (id) => {
    setExpandedContextItemIds((prev) => prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]);
  };

  const addMessageToContext = async (msg) => {
    const id = msg.id || `${msg.role}-${msg.createdAt || Date.now()}`;
    setAddingContextId(id);
    setContextStatus(null);
    try {
      const title = `${msg.role === "user" ? "Question" : "Response"}: ${String(msg.text || "").slice(0, 70)}`;
      const payload = await addAdvisorContextItem({
        type: "chat_message",
        title,
        source: "advisor_chat",
        payload: {
          role: msg.role,
          text: msg.text,
          bullets: msg.bullets || [],
          riskLevel: msg.riskLevel || null,
          symbolsMentioned: msg.symbolsMentioned || [],
          contextStats: msg.contextStats || null,
          createdAt: msg.createdAt || null,
        },
      });
      setContextItems(Array.isArray(payload?.items) ? payload.items : []);
      if (payload?.item?.id) setSelectedContextItemIds((prev) => prev.includes(payload.item.id) ? prev : [...prev, payload.item.id]);
      setContextStatus({ type: "ok", text: "Added chat item to context" });
    } catch (err) {
      setContextStatus({ type: "error", text: err?.message || "Failed to add chat item to context" });
    } finally {
      setAddingContextId(null);
    }
  };

  const addSymbolToContext = async (match) => {
    const id = match.instrumentKey || match.symbol;
    setAddingContextId(id);
    setContextStatus(null);
    try {
      const payload = await addAdvisorSymbolContext({ symbol: match.symbol, instrumentKey: match.instrumentKey });
      setContextItems(Array.isArray(payload?.items) ? payload.items : []);
      if (payload?.item?.id) setSelectedContextItemIds((prev) => prev.includes(payload.item.id) ? prev : [...prev, payload.item.id]);
      setSymbolSearch("");
      setSymbolMatches([]);
      setContextStatus({ type: "ok", text: payload?.reused ? `Using cached ${match.symbol} context` : `Added ${match.symbol} context` });
    } catch (err) {
      setContextStatus({ type: "error", text: err?.message || `Failed to add ${match.symbol}` });
    } finally {
      setAddingContextId(null);
    }
  };

  const removeSavedContext = async (id) => {
    try {
      const payload = await removeAdvisorContextItem(id);
      setContextItems(Array.isArray(payload?.items) ? payload.items : []);
      setSelectedContextItemIds((prev) => prev.filter((item) => item !== id));
      setExpandedContextItemIds((prev) => prev.filter((item) => item !== id));
    } catch (err) {
      setContextStatus({ type: "error", text: err?.message || "Failed to remove context item" });
    }
  };

  const clearSavedContext = async () => {
    try {
      const payload = await clearAdvisorContextItems();
      setContextItems(Array.isArray(payload?.items) ? payload.items : []);
      setSelectedContextItemIds([]);
      setExpandedContextItemIds([]);
      setContextStatus({ type: "ok", text: "Cleared saved context" });
    } catch (err) {
      setContextStatus({ type: "error", text: err?.message || "Failed to clear context" });
    }
  };

  const moveSymbolFromChat = async (symbol, target) => {
    const sym = String(symbol || "").toUpperCase();
    if (!sym) return;
    setMonitorActionId(`${target}-${sym}`);
    setContextStatus(null);
    try {
      if (target === "monitor") await addPositionMonitorSymbol(sym);
      else await removePositionMonitorSymbol(sym);
      setContextStatus({ type: "ok", text: `${sym} moved to ${target === "monitor" ? "Monitor" : "Normal"}` });
    } catch (err) {
      setContextStatus({ type: "error", text: err?.message || `Failed to move ${sym}` });
    } finally {
      setMonitorActionId(null);
    }
  };

  const moveSymbolsFromChat = async (symbols, target, actionId = null) => {
    const uniqueSymbols = Array.from(new Set((Array.isArray(symbols) ? symbols : [])
      .map((symbol) => String(symbol || "").toUpperCase().trim())
      .filter(Boolean)));
    if (!uniqueSymbols.length) return;
    const label = target === "monitor" ? "Monitor" : "Normal";
    setMonitorActionId(actionId || `${target}-all`);
    setContextStatus(null);
    const failed = [];
    try {
      for (const symbol of uniqueSymbols) {
        try {
          if (target === "monitor") await addPositionMonitorSymbol(symbol);
          else await removePositionMonitorSymbol(symbol);
        } catch (err) {
          failed.push(symbol);
        }
      }
      if (failed.length) {
        setContextStatus({ type: "error", text: `${uniqueSymbols.length - failed.length}/${uniqueSymbols.length} moved to ${label}. Failed: ${failed.join(", ")}` });
      } else {
        setContextStatus({ type: "ok", text: `${uniqueSymbols.length} symbols moved to ${label}` });
      }
    } finally {
      setMonitorActionId(null);
    }
  };

  const renderAdvisorActionButtons = (msg) => {
    const groups = getAdvisorActionGroups(msg.text, msg.symbolsMentioned);
    const actionSymbols = [...groups.EXIT, ...groups.PARTIAL_EXIT, ...groups.TIGHTEN_STOP];
    const knownSymbols = new Set([...actionSymbols, ...groups.WATCH, ...groups.HOLD]);
    const mentionedSymbols = Array.from(new Set((msg.symbolsMentioned || [])
      .map((symbol) => String(symbol || "").toUpperCase().trim())
      .filter(Boolean)));
    const unclassifiedSymbols = mentionedSymbols.filter((symbol) => !knownSymbols.has(symbol)).slice(0, 8);

    return (
      <div style={{ marginTop: 8, display: "flex", gap: 6, flexWrap: "wrap" }}>
        {mentionedSymbols.length > 1 && (
          <>
            <button
              type="button"
              disabled={monitorActionId === "monitor-mentioned"}
              onClick={() => moveSymbolsFromChat(mentionedSymbols, "monitor", "monitor-mentioned")}
              style={{ background: "#082f49", border: "1px solid #38bdf866", color: "#38bdf8", borderRadius: 6, padding: "4px 7px", fontSize: 9, fontWeight: 900, cursor: "pointer" }}
            >
              {monitorActionId === "monitor-mentioned" ? "Moving mentioned" : `Monitor mentioned (${mentionedSymbols.length})`}
            </button>
            <button
              type="button"
              disabled={monitorActionId === "normal-mentioned"}
              onClick={() => moveSymbolsFromChat(mentionedSymbols, "normal", "normal-mentioned")}
              style={{ background: "#052e1a", border: "1px solid #22c55e66", color: "#22c55e", borderRadius: 6, padding: "4px 7px", fontSize: 9, fontWeight: 900, cursor: "pointer" }}
            >
              {monitorActionId === "normal-mentioned" ? "Moving mentioned" : `Normal mentioned (${mentionedSymbols.length})`}
            </button>
          </>
        )}
        {actionSymbols.length > 0 && (
          <button
            type="button"
            disabled={monitorActionId === "monitor-actions"}
            onClick={() => moveSymbolsFromChat(actionSymbols, "monitor", "monitor-actions")}
            style={{ background: "#082f49", border: "1px solid #38bdf866", color: "#38bdf8", borderRadius: 6, padding: "4px 7px", fontSize: 9, fontWeight: 900, cursor: "pointer" }}
          >
            {monitorActionId === "monitor-actions" ? "Moving actions" : `Monitor actions (${actionSymbols.length})`}
          </button>
        )}
        {groups.WATCH.length > 0 && (
          <button
            type="button"
            disabled={monitorActionId === "monitor-watch"}
            onClick={() => moveSymbolsFromChat(groups.WATCH, "monitor", "monitor-watch")}
            style={{ background: "#082f49", border: "1px solid #38bdf866", color: "#38bdf8", borderRadius: 6, padding: "4px 7px", fontSize: 9, fontWeight: 900, cursor: "pointer" }}
          >
            {monitorActionId === "monitor-watch" ? "Moving watch" : `Monitor watch (${groups.WATCH.length})`}
          </button>
        )}
        {groups.HOLD.length > 0 && (
          <button
            type="button"
            disabled={monitorActionId === "normal-hold"}
            onClick={() => moveSymbolsFromChat(groups.HOLD, "normal", "normal-hold")}
            style={{ background: "#052e1a", border: "1px solid #22c55e66", color: "#22c55e", borderRadius: 6, padding: "4px 7px", fontSize: 9, fontWeight: 900, cursor: "pointer" }}
          >
            {monitorActionId === "normal-hold" ? "Moving holds" : `Normal holds (${groups.HOLD.length})`}
          </button>
        )}
        {unclassifiedSymbols.map((symbol) => (
          <span key={symbol} style={{ display: "inline-flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
            <button
              type="button"
              disabled={monitorActionId === `monitor-${symbol}`}
              onClick={() => moveSymbolFromChat(symbol, "monitor")}
              style={{ background: "#082f49", border: "1px solid #38bdf866", color: "#38bdf8", borderRadius: 6, padding: "4px 7px", fontSize: 9, fontWeight: 800, cursor: "pointer" }}
            >
              {monitorActionId === `monitor-${symbol}` ? "Moving" : `${symbol} Monitor`}
            </button>
            <button
              type="button"
              disabled={monitorActionId === `normal-${symbol}`}
              onClick={() => moveSymbolFromChat(symbol, "normal")}
              style={{ background: "#052e1a", border: "1px solid #22c55e66", color: "#22c55e", borderRadius: 6, padding: "4px 7px", fontSize: 9, fontWeight: 800, cursor: "pointer" }}
            >
              {monitorActionId === `normal-${symbol}` ? "Moving" : "Normal"}
            </button>
          </span>
        ))}
      </div>
    );
  };

  const send = async (text = input) => {
    const message = String(text || "").trim();
    if (!message || sending) return;
    const isOpenPositionShortcut = quickPrompts.includes(message);
    const isBroadOpenPositionRequest = isBroadOpenPositionMessage(message);
    const requestContextOptions = (isOpenPositionShortcut || isBroadOpenPositionRequest)
      ? {
        ...selectedContextOptions,
        symbols: (isBroadOpenPositionRequest || isOpenPositionShortcut) ? [] : selectedContextOptions.symbols,
        includePositions: true,
        includeTechnicals: true,
        includeStops: true,
        includeEntryStrategy: true,
        includeAiAnalyses: true,
        includeRiskConfig: true,
        includeRecentTrades: false,
      }
      : selectedContextOptions;
    const userDraft = { id: `draft-${Date.now()}`, role: "user", text: message, createdAt: new Date().toISOString() };
    shouldAutoScrollRef.current = true;
    setInput("");
    setSending(true);
    setError(null);
    setMessages((prev) => [...prev.filter((m) => m.id !== "intro"), userDraft]);
    try {
      const streamId = `stream-${Date.now()}`;
      let streamedText = "";
      setMessages((prev) => [...prev, { id: streamId, role: "assistant", text: "Advisor is thinking...", riskLevel: "UNKNOWN", createdAt: new Date().toISOString(), streaming: true }]);
      const response = await postAdvisorChatStream(message, requestContextOptions, {
        onDelta: (chunk) => {
          streamedText += chunk;
          setMessages((prev) => prev.map((msg) => (
            msg.id === streamId ? { ...msg, text: streamedText || "Advisor is thinking..." } : msg
          )));
        },
      }, chatModelOverride || undefined);
      const savedPair = Array.isArray(response.messages) && response.messages.length >= 2 ? response.messages : null;
      setMessages((prev) => savedPair ? [
        ...prev.filter((m) => m.id !== userDraft.id && m.id !== streamId),
        ...savedPair,
      ] : [...prev, {
        id: streamId,
        role: "assistant",
        text: response.answer || streamedText || "No answer returned.",
        bullets: response.bullets || [],
        riskLevel: response.riskLevel,
        symbolsMentioned: response.symbolsMentioned || [],
        contextStats: response.contextStats || null,
        provider: response.provider,
        model: response.model,
        attemptedProviders: response.attemptedProviders || [],
        selectedContext: response.contextStats?.selectedContext || requestContextOptions,
        createdAt: response.createdAt || new Date().toISOString(),
      }]);
    } catch (err) {
      try {
        const response = await postAdvisorChat(message, requestContextOptions, chatModelOverride || undefined);
        const savedPair = Array.isArray(response.messages) && response.messages.length >= 2 ? response.messages : null;
        setMessages((prev) => savedPair ? [
          ...prev.filter((m) => m.id !== userDraft.id && !m.streaming),
          ...savedPair,
        ] : [...prev.filter((m) => !m.streaming), {
          role: "assistant",
          text: response.answer || "No answer returned.",
          bullets: response.bullets || [],
          riskLevel: response.riskLevel,
          symbolsMentioned: response.symbolsMentioned || [],
          contextStats: response.contextStats || null,
          provider: response.provider,
          model: response.model,
          attemptedProviders: response.attemptedProviders || [],
          selectedContext: response.contextStats?.selectedContext || requestContextOptions,
          createdAt: response.createdAt || new Date().toISOString(),
        }]);
      } catch (fallbackErr) {
        const msg = fallbackErr?.message || err?.message || "Advisor chat failed";
        setError(msg);
        setMessages((prev) => [...prev.filter((m) => !m.streaming), { role: "assistant", text: msg, riskLevel: "UNKNOWN", createdAt: new Date().toISOString(), error: true }]);
      }
    } finally {
      setSending(false);
      loadContext();
      loadWorkerUsage();
    }
  };

  const loadOlderMessages = async () => {
    if (loadingHistory || !hasMoreHistory || !historyBefore) return;
    const el = messagesRef.current;
    const previousHeight = el?.scrollHeight || 0;
    loadingOlderRef.current = true;
    shouldAutoScrollRef.current = false;
    setLoadingHistory(true);
    try {
      const payload = await getAdvisorChatHistory({ limit: 2, before: historyBefore, days: historyDays || undefined });
      const older = payload?.messages || [];
      setMessages((prev) => [...older, ...prev.filter((msg) => msg.id !== "intro")]);
      setHistoryBefore(payload?.nextBefore || historyBefore);
      setHasMoreHistory(!!payload?.hasMore);
      setTimeout(() => {
        if (el) el.scrollTop = (el.scrollHeight || 0) - previousHeight;
        loadingOlderRef.current = false;
      }, 0);
    } catch (err) {
      loadingOlderRef.current = false;
      setError(err?.message || "Failed to load older chat messages");
    } finally {
      setLoadingHistory(false);
    }
  };

  const advisorChatStatus = botStatus?.advisor?.chat || botStatus?.advisor || {};
  const disabledReason = !botStatus?.advisor?.enabled
    ? "Advisor disabled"
    : !advisorChatStatus.apiKeyConfigured
      ? `${advisorChatStatus.provider || "AI"} key missing`
      : null;

  const providerLabel = advisorChatStatus.provider === "cloudflare_workers_ai"
    ? "Cloudflare Workers AI"
    : advisorChatStatus.provider === "gemini"
      ? "Gemini"
    : advisorChatStatus.provider === "nvidia"
      ? "NVIDIA NIM"
      : advisorChatStatus.provider || "advisor";
  const modelLabel = advisorChatStatus.model || "";
  const nvidiaChatModels = Array.from(new Set((advisorChatStatus.nvidiaModels || [])
    .map((model) => String(model || "").trim())
    .filter(Boolean)));
  const chatModelOptions = advisorChatStatus.nvidiaConfigured
    ? nvidiaChatModels.filter((model) => model !== modelLabel)
    : [];
  const fallbackProviders = Array.isArray(advisorChatStatus.fallbackProviders)
    ? advisorChatStatus.fallbackProviders
    : advisorChatStatus.fallbackProvider
      ? [advisorChatStatus.fallbackProvider]
      : [];
  const fallbackLabel = fallbackProviders
    .map((provider) => provider === "gemini" ? "Gemini" : provider === "gemini_flash_lite" ? "Gemini Flash-Lite" : provider === "cloudflare_workers_ai" ? "Cloudflare Workers AI" : provider === "groq" ? "Groq" : String(provider).startsWith("nvidia_chat_") ? "NVIDIA NIM" : provider)
    .join(" -> ");
  const providerReady = !disabledReason;
  const hasWorkerFallback = Number(botStatus?.advisor?.workerConfiguredCount || 0) > 0;
  const totalRemainingNeurons = Array.isArray(workerUsage?.workers)
    ? workerUsage.workers.reduce((sum, worker) => {
      const remaining = Number(worker.remainingFreeNeurons);
      return Number.isFinite(remaining) ? sum + remaining : sum;
    }, 0)
    : null;

  const ContextCheck = ({ id, label, detail }) => (
    <label style={{ display: "flex", gap: 9, alignItems: "flex-start", color: "#cbd5e1", fontSize: 11, lineHeight: 1.35, cursor: "pointer" }}>
      <input
        type="checkbox"
        checked={!!contextOptions[id]}
        onChange={() => setContextFlag(id)}
        style={{ marginTop: 2, accentColor: "#38bdf8" }}
      />
      <span>
        <span style={{ display: "block", fontWeight: 800 }}>{label}</span>
        <span style={{ display: "block", color: "#64748b", fontSize: 10 }}>{detail}</span>
      </span>
    </label>
  );

  const shellHeight = isMobile ? "auto" : "calc(100vh - 118px)";
  const chatPanelHeight = isMobile ? "calc(100dvh - 20px)" : "100%";

  return (
    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "minmax(0,1fr)" : "minmax(0,1fr) 340px", gap: isMobile ? 10 : 14, width: "100%", maxWidth: "100%", minWidth: 0, boxSizing: "border-box", height: shellHeight, minHeight: isMobile ? "auto" : 0, overflowX: "hidden", overflowY: isMobile ? "visible" : "hidden", alignItems: "stretch" }}>
      <div style={{ background: "#060d1a", border: "1px solid #1e293b", borderRadius: isMobile ? 8 : 10, width: "100%", maxWidth: "100%", minWidth: 0, boxSizing: "border-box", height: chatPanelHeight, minHeight: isMobile ? 560 : 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <div style={{ padding: isMobile ? "10px 12px" : "14px 16px", borderBottom: "1px solid #0f172a", display: "flex", justifyContent: "space-between", gap: 10, alignItems: isMobile ? "flex-start" : "center", flexWrap: "wrap", flexShrink: 0, minWidth: 0 }}>
          <div style={{ minWidth: 0, maxWidth: "100%" }}>
            <div style={{ fontFamily: "'Syne',sans-serif", fontSize: isMobile ? 20 : 18, fontWeight: 800, color: "#f1f5f9" }}>Advisor Chat</div>
            <div style={{ fontSize: 11, color: "#64748b", overflowWrap: "anywhere" }}>Read-only portfolio assistant. You choose what backend context is sent.</div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: isMobile ? "flex-start" : "flex-end", gap: 3, minWidth: 0, maxWidth: "100%" }}>
            <div style={{ color: disabledReason ? "#f59e0b" : "#22c55e", fontSize: 11, fontFamily: "'DM Mono',monospace", fontWeight: 700 }}>
              {disabledReason || `● ${providerLabel.toUpperCase()} ready`}
            </div>
            {providerReady && modelLabel && (
              <div style={{ color: "#475569", fontSize: 10, fontFamily: "'DM Mono',monospace", maxWidth: "100%", overflowWrap: "anywhere" }}>
                {modelLabel}
              </div>
            )}
            {chatModelOptions.length > 1 && (
              <label style={{ display: "flex", alignItems: "center", gap: 6, color: "#64748b", fontSize: 9, fontFamily: "'DM Mono',monospace", maxWidth: "100%" }}>
                This chat
                <select
                  value={chatModelOverride}
                  disabled={sending}
                  onChange={(event) => setChatModelOverride(event.target.value)}
                  style={{ minWidth: 0, maxWidth: isMobile ? "56vw" : 250, background: "#07111f", border: "1px solid #1e293b", borderRadius: 5, color: "#cbd5e1", fontSize: 9, padding: "3px 5px" }}
                >
                  <option value="">Default: {modelLabel}</option>
                  {chatModelOptions.map((model) => <option key={model} value={model}>{model}</option>)}
                </select>
              </label>
            )}
          </div>
        </div>
        {providerReady && (
          <div style={{ padding: isMobile ? "7px 12px" : "8px 14px", background: "#0d1a0d", borderBottom: "1px solid #166534", fontSize: 10, color: "#4ade80", display: "flex", alignItems: "center", gap: 6, flexShrink: 0, minWidth: 0, maxWidth: "100%", whiteSpace: isMobile ? "nowrap" : "normal", overflowX: "auto", boxSizing: "border-box" }}>
            <span style={{ fontWeight: 700 }}>PRIMARY: {providerLabel}</span>
            <span style={{ color: "#334155" }}>·</span>
            {hasWorkerFallback && <span style={{ color: "#64748b" }}>Cloudflare fallback: {botStatus?.advisor?.workerKeyCount || 0} worker keys configured</span>}
            {fallbackLabel && <span style={{ color: "#64748b" }}>fallback: {fallbackLabel}</span>}
          </div>
        )}

        <div style={{ padding: isMobile ? "8px 10px" : 12, display: "flex", gap: 8, flexWrap: isMobile ? "nowrap" : "wrap", overflowX: isMobile ? "auto" : "hidden", overflowY: "hidden", borderBottom: "1px solid #0f172a", flexShrink: 0, width: "100%", minWidth: 0, maxWidth: "100%", boxSizing: "border-box", WebkitOverflowScrolling: "touch" }}>
          {quickPrompts.map((prompt) => (
            <button
              key={prompt}
              type="button"
              disabled={sending}
              onClick={() => send(prompt)}
              style={{ background: "#07111f", border: "1px solid #1e293b", color: "#94a3b8", borderRadius: 7, padding: "7px 9px", fontSize: 10, cursor: sending ? "not-allowed" : "pointer", flex: isMobile ? "0 0 236px" : "0 1 auto", maxWidth: isMobile ? "78vw" : "none", minWidth: 0, whiteSpace: "normal", textAlign: "left" }}
            >
              {prompt}
            </button>
          ))}
        </div>

        <div style={{ padding: "8px 12px", borderBottom: "1px solid #0f172a", color: "#64748b", fontSize: 10, flexShrink: 0, minWidth: 0, maxWidth: "100%", boxSizing: "border-box", whiteSpace: isMobile ? "normal" : "nowrap", overflowX: "hidden", overflowWrap: "anywhere" }}>
          Context: {contextLabels.length ? contextLabels.join(" / ") : "summary only"}
          {selectedContextOptions.symbols.length ? ` | Symbols: ${selectedContextOptions.symbols.join(", ")}` : ""}
        </div>

        <div
          ref={messagesRef}
          onScroll={(e) => {
            shouldAutoScrollRef.current = isNearChatBottom(e.currentTarget);
            if (e.currentTarget.scrollTop < 24) loadOlderMessages();
          }}
          style={{ flex: 1, minHeight: 0, minWidth: 0, maxWidth: "100%", overflowY: "auto", overflowX: "hidden", padding: isMobile ? "10px 12px" : 14, display: "flex", flexDirection: "column", gap: 12, boxSizing: "border-box" }}
        >
          {hasMoreHistory && (
            <button type="button" disabled={loadingHistory} onClick={loadOlderMessages} style={{ alignSelf: "center", background: "#07111f", border: "1px solid #1e293b", color: "#94a3b8", borderRadius: 7, padding: "6px 9px", fontSize: 10, cursor: loadingHistory ? "not-allowed" : "pointer" }}>
              {loadingHistory ? "Loading..." : "Load older"}
            </button>
          )}
          {messages.map((msg, index) => (
            <div key={msg.id || index} style={{ alignSelf: msg.role === "user" ? "flex-end" : "flex-start", width: "fit-content", maxWidth: isMobile ? "100%" : "86%", minWidth: 0 }}>
              <div style={{
                background: msg.role === "user" ? "#0d1f35" : msg.error ? "#2d0a0a" : "#07111f",
                border: `1px solid ${msg.role === "user" ? "#38bdf866" : msg.error ? "#ef444466" : "#1e293b"}`,
                color: msg.role === "user" ? "#e0f2fe" : msg.error ? "#f87171" : "#e2e8f0",
                borderRadius: 8,
                padding: "10px 12px",
                fontSize: 12,
                lineHeight: 1.55,
                whiteSpace: "pre-wrap",
                maxWidth: "100%",
                minWidth: 0,
                boxSizing: "border-box",
                overflowWrap: "anywhere",
                wordBreak: "break-word",
              }}>
                {msg.role === "assistant" && (
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", color: "#64748b", fontSize: 10, fontWeight: 800, marginBottom: 5 }}>
                    <span>{messageAiLabel(msg, modelLabel)}</span>
                    {msg.riskLevel && msg.riskLevel !== "UNKNOWN" && (
                      <span style={{ color: riskColor(msg.riskLevel) }}>Risk: {msg.riskLevel}</span>
                    )}
                  </div>
                )}
                <MarkdownMessage text={msg.text} isMobile={isMobile} />
                {Array.isArray(msg.bullets) && msg.bullets.length > 0 && (
                  <ul style={{ margin: "8px 0 0 18px", padding: 0 }}>
                    {msg.bullets.map((item, i) => <li key={i}>{item}</li>)}
                  </ul>
                )}
                {Array.isArray(msg.symbolsMentioned) && msg.symbolsMentioned.length > 0 && (
                  <div style={{ marginTop: 8, color: "#38bdf8", fontSize: 10 }}>
                    {msg.symbolsMentioned.join(" · ")}
                  </div>
                )}
                {msg.role === "assistant" && Array.isArray(msg.symbolsMentioned) && msg.symbolsMentioned.length > 0 && !msg.streaming && (
                  renderAdvisorActionButtons(msg)
                )}
                {msg.contextStats && (
                  <div style={{ marginTop: 8, color: "#64748b", fontSize: 9 }}>
                    Sent: {msg.contextStats.sentPositions || 0} positions, {msg.contextStats.sentAnalyses || 0} analyses, {msg.contextStats.sentSavedContext || 0} context, {msg.contextStats.sentRecentTrades || 0} trades
                  </div>
                )}
                {msg.text && !msg.streaming && (
                  <button
                    type="button"
                    disabled={addingContextId === (msg.id || `${msg.role}-${msg.createdAt || index}`)}
                    onClick={() => addMessageToContext(msg)}
                    style={{ marginTop: 8, background: "#07111f", border: "1px solid #38bdf866", color: "#38bdf8", borderRadius: 6, padding: "4px 7px", fontSize: 9, fontWeight: 800, cursor: "pointer" }}
                  >
                    {addingContextId === (msg.id || `${msg.role}-${msg.createdAt || index}`) ? "Adding" : "Add ctx"}
                  </button>
                )}
              </div>
              <div style={{ color: "#334155", fontSize: 9, marginTop: 4, textAlign: msg.role === "user" ? "right" : "left" }}>
                {formatIstDateTime(msg.createdAt)}
              </div>
            </div>
          ))}
          {sending && <div style={{ color: "#64748b", fontSize: 12 }}>Advisor is thinking...</div>}
          {!sending && loadingHistory && !messages.length && <div style={{ color: "#64748b", fontSize: 12 }}>Loading chat...</div>}
          <div ref={endRef} />
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            send();
          }}
          style={{ padding: isMobile ? 10 : 12, borderTop: "1px solid #0f172a", display: "flex", gap: 8, flexShrink: 0, minWidth: 0, maxWidth: "100%", boxSizing: "border-box" }}
        >
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask about portfolio risk, giveback, stops, or a symbol..."
            style={{ flex: 1, minWidth: 0, background: "#07111f", border: "1px solid #1e293b", color: "#e2e8f0", borderRadius: 8, padding: "10px 12px", fontSize: 12 }}
          />
          <button type="submit" disabled={sending || !input.trim()} style={{ background: "#052e1a", border: "1px solid #22c55e66", color: sending || !input.trim() ? "#166534" : "#22c55e", borderRadius: 8, padding: "10px 14px", fontSize: 12, fontWeight: 800, cursor: sending || !input.trim() ? "not-allowed" : "pointer" }}>
            Send
          </button>
        </form>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 10, width: "100%", maxWidth: "100%", minWidth: 0, boxSizing: "border-box", height: isMobile ? "auto" : "100%", minHeight: 0, overflowX: "hidden", overflowY: isMobile ? "visible" : "auto", paddingBottom: isMobile ? 80 : 0 }}>
        <div style={{ background: "#060d1a", border: "1px solid #1e293b", borderRadius: 10, padding: 14, flexShrink: 0, width: "100%", maxWidth: "100%", minWidth: 0, boxSizing: "border-box" }}>
          <div style={{ fontSize: 10, color: "#475569", fontWeight: 800, letterSpacing: "0.08em" }}>CONTEXT TO SEND</div>
          <div style={{ marginTop: 10 }}>
            <input
              value={symbolsText}
              onChange={(e) => setSymbolsText(e.target.value)}
              placeholder="Optional symbols: CDSL, KRBL"
              style={{ width: "100%", boxSizing: "border-box", background: "#07111f", border: "1px solid #1e293b", color: "#e2e8f0", borderRadius: 7, padding: "8px 9px", fontSize: 11 }}
            />
          </div>
          <label style={{ marginTop: 10, display: "flex", gap: 8, alignItems: "center", color: "#94a3b8", fontSize: 11, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={allBaseContextSelected}
              onChange={(e) => setAllBaseContext(e.target.checked)}
              style={{ accentColor: "#38bdf8" }}
            />
            <span>Select all context sections</span>
          </label>
          <div style={{ marginTop: 12, display: "grid", gap: 10 }}>
            <ContextCheck id="includePositions" label="Positions" detail="Qty, entry, LTP, P&L, giveback." />
            <ContextCheck id="includeTechnicals" label="Technicals" detail="RSI, RVOL, volume, Mercury, MACD." />
            <ContextCheck id="includeStops" label="Stops/Targets" detail="Current stop, initial stop, take-profit." />
            <ContextCheck id="includeEntryStrategy" label="Entry Strategy" detail="Entry score, reasons, screener labels." />
            <ContextCheck id="includeAiAnalyses" label="Saved AI Analysis" detail="Latest manual/deep scan summaries." />
            <ContextCheck id="includeSavedContext" label="Saved Context" detail="Temporary chat, scanner rows, and symbol candles." />
            <ContextCheck id="includeRiskConfig" label="Risk Config" detail="Capital, max positions, risk settings." />
            <ContextCheck id="includeRecentTrades" label="Recent Trades" detail="Recent closed-trade outcomes." />
          </div>
          <div style={{ marginTop: 14, borderTop: "1px solid #0f172a", paddingTop: 12 }}>
            <div style={{ fontSize: 10, color: "#475569", fontWeight: 800, letterSpacing: "0.08em" }}>ADD SYMBOL CONTEXT</div>
            <input
              value={symbolSearch}
              onChange={(e) => setSymbolSearch(e.target.value.toUpperCase())}
              placeholder="Search symbol: RELIANCE"
              style={{ marginTop: 8, width: "100%", boxSizing: "border-box", background: "#07111f", border: "1px solid #1e293b", color: "#e2e8f0", borderRadius: 7, padding: "8px 9px", fontSize: 11 }}
            />
            {symbolSearchLoading && <div style={{ color: "#64748b", fontSize: 10, marginTop: 6 }}>Searching...</div>}
            {symbolMatches.length > 0 && (
              <div style={{ marginTop: 8, display: "grid", gap: 6, maxHeight: 170, overflowY: "auto" }}>
                {symbolMatches.map((match) => (
                  <div key={match.instrumentKey || match.symbol} style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center", borderBottom: "1px solid #0f172a", paddingBottom: 6 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ color: "#e2e8f0", fontSize: 11, fontWeight: 800 }}>{match.symbol}</div>
                      <div style={{ color: "#475569", fontSize: 9, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{match.name || match.instrumentKey}</div>
                    </div>
                    <button
                      type="button"
                      disabled={addingContextId === (match.instrumentKey || match.symbol)}
                      onClick={() => addSymbolToContext(match)}
                      style={{ background: "#0d1f35", border: "1px solid #38bdf866", color: "#38bdf8", borderRadius: 6, padding: "5px 8px", fontSize: 9, fontWeight: 800, cursor: "pointer", whiteSpace: "nowrap" }}
                    >
                      {addingContextId === (match.instrumentKey || match.symbol) ? "Adding" : "Add"}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div style={{ marginTop: 14, borderTop: "1px solid #0f172a", paddingTop: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
              <div style={{ fontSize: 10, color: "#475569", fontWeight: 800, letterSpacing: "0.08em" }}>SAVED CONTEXT ({contextItems.length})</div>
              {contextItems.length > 0 && (
                <button type="button" onClick={clearSavedContext} style={{ background: "transparent", border: "none", color: "#f87171", fontSize: 9, cursor: "pointer" }}>Clear</button>
              )}
            </div>
            {contextStatus && <div style={{ color: contextStatus.type === "ok" ? "#22c55e" : "#f59e0b", fontSize: 10, marginTop: 6 }}>{contextStatus.text}</div>}
            {contextItems.length > 0 && (
              <label style={{ marginTop: 8, display: "flex", gap: 8, alignItems: "center", color: "#94a3b8", fontSize: 10, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={allSavedContextSelected}
                  onChange={(e) => setAllSavedContextItems(e.target.checked)}
                  style={{ accentColor: "#38bdf8" }}
                />
                <span>Select all saved items</span>
              </label>
            )}
            <div style={{ marginTop: 8, display: "grid", gap: 6, maxHeight: 180, overflowY: "auto" }}>
              {contextItems.length === 0 ? (
                <div style={{ color: "#64748b", fontSize: 10 }}>No temporary context saved.</div>
              ) : contextItems.slice().reverse().map((item) => (
                <div key={item.id} style={{ borderBottom: "1px solid #0f172a", paddingBottom: 6, minWidth: 0 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "flex-start" }}>
                    <label style={{ display: "flex", gap: 8, minWidth: 0, cursor: "pointer" }}>
                      <input
                        type="checkbox"
                        checked={selectedContextItemIds.includes(item.id)}
                        onChange={() => toggleSavedContextItem(item.id)}
                        style={{ marginTop: 2, accentColor: "#38bdf8" }}
                      />
                      <span style={{ minWidth: 0 }}>
                        <span style={{ display: "block", color: "#cbd5e1", fontSize: 10, fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.title}</span>
                        <span style={{ display: "block", color: "#475569", fontSize: 9 }}>{item.type}{item.symbol ? ` - ${item.symbol}` : ""}</span>
                      </span>
                    </label>
                    <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                      <button type="button" onClick={() => toggleExpandedContextItem(item.id)} style={{ background: "transparent", border: "none", color: "#38bdf8", fontSize: 9, cursor: "pointer" }}>
                        {expandedContextItemIds.includes(item.id) ? "Hide" : "View"}
                      </button>
                      <button type="button" onClick={() => removeSavedContext(item.id)} style={{ background: "transparent", border: "none", color: "#64748b", fontSize: 12, cursor: "pointer" }}>x</button>
                    </div>
                  </div>
                  {expandedContextItemIds.includes(item.id) && (
                    <pre style={{ margin: "7px 0 0 24px", maxHeight: 160, overflow: "auto", whiteSpace: "pre-wrap", overflowWrap: "anywhere", color: "#64748b", fontSize: 9, lineHeight: 1.45, background: "#020617", border: "1px solid #0f172a", borderRadius: 6, padding: 8 }}>
                      {JSON.stringify(item.payload || {}, null, 2)}
                    </pre>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div style={{ background: "#060d1a", border: "1px solid #1e293b", borderRadius: 10, padding: 14, flexShrink: 0, width: "100%", maxWidth: "100%", minWidth: 0, boxSizing: "border-box" }}>
          <div style={{ fontSize: 10, color: "#475569", fontWeight: 800, letterSpacing: "0.08em" }}>HISTORY</div>
          <div style={{ marginTop: 10, display: "flex", gap: 8 }}>
            <select
              value={historyDays}
              onChange={(e) => setHistoryDays(e.target.value)}
              style={{ flex: 1, background: "#07111f", border: "1px solid #1e293b", color: "#e2e8f0", borderRadius: 7, padding: "8px 9px", fontSize: 11 }}
            >
              <option value="1">1 day</option>
              <option value="2">2 days</option>
              <option value="3">3 days</option>
              <option value="7">7 days</option>
              <option value="10">10 days</option>
              <option value="30">30 days</option>
            </select>
          </div>
        </div>

        <div style={{ background: "#060d1a", border: "1px solid #1e293b", borderRadius: 10, padding: 14, flexShrink: 0, width: "100%", maxWidth: "100%", minWidth: 0, boxSizing: "border-box" }}>
          <div style={{ fontSize: 10, color: "#475569", fontWeight: 800, letterSpacing: "0.08em" }}>AVAILABLE</div>
          <div style={{ marginTop: 10, display: "grid", gap: 8, fontSize: 12, color: "#94a3b8" }}>
            <div>Open positions: <strong style={{ color: "#e2e8f0" }}>{botStatus?.openPositions ?? 0}</strong></div>
            <div>Saved AI analyses: <strong style={{ color: "#e2e8f0" }}>{Object.keys(analyses || {}).length}</strong></div>
            <div>Market: <strong style={{ color: botStatus?.marketOpen ? "#22c55e" : "#64748b" }}>{botStatus?.marketOpen ? "open" : "closed"}</strong></div>
            <div>Provider: <strong style={{ color: "#38bdf8" }}>{providerLabel.toUpperCase()}</strong></div>
            <div>Model: <strong style={{ color: "#e2e8f0" }}>{modelLabel || "-"}</strong></div>
          </div>
        </div>

        {hasWorkerFallback && (
          <div style={{ background: "#060d1a", border: "1px solid #1e293b", borderRadius: 10, padding: 14, flexShrink: 0, width: "100%", maxWidth: "100%", minWidth: 0, boxSizing: "border-box" }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
              <div style={{ fontSize: 10, color: "#475569", fontWeight: 800, letterSpacing: "0.08em" }}>CLOUDFLARE USAGE</div>
              <button
                type="button"
                onClick={() => loadWorkerUsage(true)}
                disabled={usageLoading}
                style={{
                  background: "#07111f",
                  border: "1px solid #1e293b",
                  color: usageLoading ? "#64748b" : "#38bdf8",
                  borderRadius: 6,
                  padding: "4px 7px",
                  fontSize: 9,
                  fontWeight: 800,
                  cursor: usageLoading ? "wait" : "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                {usageLoading ? "Loading" : "Refresh"}
              </button>
            </div>
            <div style={{ color: "#475569", fontSize: 9, marginTop: 5, lineHeight: 1.45 }}>
              Cloudflare GraphQL readout. No local usage estimate is used.
            </div>
            <div style={{ color: workerUsage?.error ? "#f59e0b" : "#64748b", fontSize: 9, marginTop: 5, lineHeight: 1.45 }}>
              {workerUsage?.error
                ? workerUsage.error
                : `${workerUsage?.cached ? "Cached" : "Live"}${workerUsage?.stale ? " stale" : ""}${workerUsage?.updatedAt ? ` · ${formatIstDateTime(workerUsage.updatedAt)}` : ""}`}
            </div>
            {totalRemainingNeurons != null && (
              <div style={{ marginTop: 8, color: "#22c55e", fontSize: 12, fontWeight: 800 }}>
                Neurons left today: {fmtNumber(totalRemainingNeurons)}
              </div>
            )}
            <div style={{ marginTop: 10, display: "grid", gap: 10 }}>
              {(workerUsage?.workers || []).length ? workerUsage.workers.map((worker) => {
                const statusColor = worker.available ? "#22c55e" : "#f59e0b";
                const hasRows = Array.isArray(worker.rows) && worker.rows.length > 0;
                const errorRequests = hasRows
                  ? worker.rows.reduce((sum, row) => sum + (Number(row.errorCode || 0) ? Number(row.count || 0) : 0), 0)
                  : 0;
                const successRequests = Math.max(0, Number(worker.totalRequests || 0) - errorRequests);
                return (
                  <div key={worker.index} style={{ borderTop: "1px solid #0f172a", paddingTop: 8 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 8, fontSize: 11 }}>
                      <strong style={{ color: "#e2e8f0" }}>ACCOUNT_{worker.index}</strong>
                      <span style={{ color: worker.quotaExceeded ? "#f59e0b" : statusColor }}>
                        {worker.stale ? "stale" : worker.quotaExceeded ? "quota exhausted" : worker.available ? "reachable" : `HTTP ${worker.status || "-"}`}
                      </span>
                    </div>
                    <div style={{ color: "#475569", fontSize: 9, marginTop: 2 }}>{worker.account || "account configured"}</div>
                    {worker.available ? (
                      <div style={{ marginTop: 6, display: "grid", gap: 3, fontSize: 10, color: "#94a3b8" }}>
                        <div>Requests: <strong style={{ color: "#e2e8f0" }}>{fmtNumber(worker.totalRequests)}</strong></div>
                        <div>Neurons: <strong style={{ color: "#e2e8f0" }}>{fmtNumber(worker.totalNeurons)}</strong> / {fmtNumber(workerUsage?.dailyFreeNeurons)}</div>
                        <div>Remaining: <strong style={{ color: worker.quotaExceeded ? "#f59e0b" : "#e2e8f0" }}>{worker.remainingFreeNeurons == null ? "-" : fmtNumber(worker.remainingFreeNeurons)}</strong></div>
                        <div>Input tokens: <strong style={{ color: "#e2e8f0" }}>{fmtNumber(worker.totalInputTokens)}</strong></div>
                        <div>Output tokens: <strong style={{ color: "#e2e8f0" }}>{fmtNumber(worker.totalOutputTokens)}</strong></div>
                        {hasRows && (
                          <div>
                            Success/errors: <strong style={{ color: "#e2e8f0" }}>{fmtNumber(successRequests)}</strong> / <strong style={{ color: errorRequests ? "#f59e0b" : "#e2e8f0" }}>{fmtNumber(errorRequests)}</strong>
                          </div>
                        )}
                        {worker.quotaExceeded && <div style={{ color: "#f59e0b", lineHeight: 1.35 }}>{worker.note}</div>}
                        {worker.stale && <div style={{ color: "#f59e0b", lineHeight: 1.35 }}>Last good read shown. Refresh error: {worker.refreshError || "usage refresh failed"}</div>}
                        {!hasRows && <div style={{ color: "#64748b", lineHeight: 1.35 }}>{worker.note}</div>}
                      </div>
                    ) : (
                      <div style={{ color: "#f59e0b", fontSize: 10, marginTop: 6, lineHeight: 1.35 }}>{worker.error || "Usage unavailable"}</div>
                    )}
                  </div>
                );
              }) : (
                <div style={{ color: "#64748b", fontSize: 11 }}>No usage tokens configured.</div>
              )}
            </div>
            <div style={{ color: "#475569", fontSize: 9, marginTop: 8 }}>
              UTC reset {workerUsage?.resetAt ? formatIstDateTime(workerUsage.resetAt) : "00:00 UTC"}
            </div>
          </div>
        )}

        <div style={{ background: "#060d1a", border: "1px solid #1e293b", borderRadius: 10, padding: 14, flexShrink: 0, width: "100%", maxWidth: "100%", minWidth: 0, boxSizing: "border-box" }}>
          <div style={{ fontSize: 10, color: "#475569", fontWeight: 800, letterSpacing: "0.08em" }}>DEEP SCAN</div>
          <div style={{ marginTop: 10, color: "#94a3b8", fontSize: 12, lineHeight: 1.55 }}>
            {deepScan?.running
              ? `Running ${deepScan.completed || 0}/${deepScan.total || 0}${deepScan.currentSymbol ? ` · ${deepScan.currentSymbol}` : ""}`
              : deepScan?.finishedAt
                ? `Last finished ${formatIstDateTime(deepScan.finishedAt)}`
                : "No deep scan has run yet."}
          </div>
        </div>

        <div style={{ background: "#060d1a", border: "1px solid #1e293b", borderRadius: 10, padding: 14, flex: isMobile ? "none" : 1, width: "100%", maxWidth: "100%", minWidth: 0, boxSizing: "border-box", minHeight: 180, maxHeight: isMobile ? 360 : "none", display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <div style={{ fontSize: 10, color: "#475569", fontWeight: 800, letterSpacing: "0.08em", flexShrink: 0 }}>LATEST ANALYSIS</div>
          <div style={{ marginTop: 10, display: "grid", gap: 10, overflowY: "auto", minHeight: 0, paddingRight: 4 }}>
            {analysisRows.length === 0 ? (
              <div style={{ color: "#64748b", fontSize: 12 }}>No saved analysis yet.</div>
            ) : analysisRows.map((row) => (
              <div key={row.symbol} style={{ borderBottom: "1px solid #0f172a", paddingBottom: 8 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                  <strong style={{ color: "#38bdf8", fontSize: 12 }}>{row.symbol}</strong>
                  <span style={{ color: riskColor(Number(row.exitUrgency || 0) >= 75 ? "HIGH" : Number(row.exitUrgency || 0) >= 45 ? "MEDIUM" : "LOW"), fontSize: 11 }}>
                    {row.exitUrgency ?? 0}/100
                  </span>
                </div>
                <div style={{ color: "#94a3b8", fontSize: 11, marginTop: 4 }}>{row.action || "WATCH"}</div>
                <div style={{ color: "#64748b", fontSize: 10, marginTop: 3, lineHeight: 1.45 }}>{row.summary || "-"}</div>
              </div>
            ))}
          </div>
        </div>

        {error && <div style={{ background: "#1f0a0a", border: "1px solid #7f1d1d44", color: "#f87171", borderRadius: 8, padding: 10, fontSize: 12 }}>{error}</div>}
      </div>
    </div>
  );
}
